import { Attachment, Button, useModal, withModalConditionalRender } from '@billy/design-system'
import { ReactElement, useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { AttachmentPreviewModal as AttachmentPreviewModalComponent } from '@features/attachments/elements/AttachmentPreviewModal'
import { useGetBill } from '@features/bills/hooks/useGetBill'
import { useFetchDaybookTransaction } from '@features/daybook/hooks/useFetchDaybookTransaction'
import { TransactionOriginatorType } from '@features/transactions/enums/transactionOriginatorType'
import { useInvoice } from '@views/invoices/hooks/useInvoice'

import { ModalId } from '../../../../../enums/modalId'
import { AttachmentPreviewTarget } from '../types/attachmentPreviewTarget'

const AttachmentPreviewModal = withModalConditionalRender(AttachmentPreviewModalComponent)

const TRANSACTIONS_ATTACHMENT_PREVIEW_MODAL_ID = `${ModalId.AttachmentPreviewModal}-transactions-table`

interface UseTransactionAttachmentPreviewResult {
  loadingTransactionId: string | undefined
  modal: ReactElement | null
  openAttachmentPreview: (target: AttachmentPreviewTarget) => void
}

export const useTransactionAttachmentPreview = (): UseTransactionAttachmentPreviewResult => {
  const { t } = useTranslation()
  const [transactionToPreview, setTransactionToPreview] = useState<AttachmentPreviewTarget | undefined>(undefined)
  const { originatorId, originatorType, transactionId } = transactionToPreview ?? {}

  const { open: openModal, close: closeModal } = useModal(TRANSACTIONS_ATTACHMENT_PREVIEW_MODAL_ID, {
    onClose: () => setTransactionToPreview(undefined),
  })

  const isBill = originatorType === TransactionOriginatorType.Bill
  const isDaybookTransaction = originatorType === TransactionOriginatorType.DaybookTransaction
  const isInvoice = originatorType === TransactionOriginatorType.Invoice

  const { data: billData, isLoading: isBillLoading } = useGetBill(isBill ? originatorId : undefined)
  const { daybookTransaction, isLoading: isDaybookLoading } = useFetchDaybookTransaction(
    isDaybookTransaction ? originatorId : undefined,
  )
  const { data: invoiceData, isLoading: isInvoiceLoading } = useInvoice(isInvoice ? originatorId : undefined)

  let attachment: Attachment | undefined
  let isLoading = false

  /* We're loading only the first attachment */
  /* Or scroll to the very first */
  if (isBill) {
    attachment = billData?.bill.attachments?.[0]
    isLoading = isBillLoading
  } else if (isDaybookTransaction) {
    attachment = daybookTransaction?.attachments?.[0]
    isLoading = isDaybookLoading
  } else if (isInvoice) {
    attachment = invoiceData?.invoice.attachments?.[0]
    isLoading = isInvoiceLoading
  }

  useEffect(() => {
    if (attachment) {
      openModal()
    }
  }, [attachment, openModal])

  const openAttachmentPreview = useCallback((target: AttachmentPreviewTarget) => {
    setTransactionToPreview(target)
  }, [])

  const modalComponent = attachment ? (
    <AttachmentPreviewModal
      attachment={attachment}
      footerButton={
        <Button fullWidth variant="tonal" onClick={closeModal}>
          {t('close')}
        </Button>
      }
      id={TRANSACTIONS_ATTACHMENT_PREVIEW_MODAL_ID}
    />
  ) : null

  return {
    loadingTransactionId: isLoading ? transactionId : undefined,
    modal: modalComponent,
    openAttachmentPreview,
  }
}
